import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, ChevronDown, ChevronRight, ExternalLink, Loader2, Search } from 'lucide-react';
import { apiGet } from '@/lib/api';

/** Which textbook each school uses, built from what students upload. */
export function BookShelf() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ board: '', class_level: '', school: '', q: '' });
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
      const res = await apiGet(`/admin/book-shelf${qs ? `?${qs}` : ''}`);
      setRows(res.shelf || []);
      setTotals(res.totals || null);
    } catch (e) { toast.error(e?.message || 'Could not load the book shelf'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="book-shelf-panel">
      <h3 className="font-heading text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-indigo-500" /> Book Shelf
      </h3>
      <p className="font-body text-xs text-slate-500 mt-0.5">
        Which book each school uses, per class and subject — built from student uploads. Two books for
        the same class and subject are flagged, so you can decide which one to build material for.
      </p>

      {totals && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="book-shelf-totals">
          {[['books', 'books'], ['schools', 'schools'], ['chapters', 'chapters'],
            ['pending', 'awaiting review'], ['unnamed', 'without a book name']].map(([key, label]) => (
            <span key={key} className="px-2.5 py-1 rounded-full bg-slate-100 font-body text-[11px] text-slate-600">
              <span className="font-heading text-slate-900">{totals[key]}</span> {label}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
        {[['q', 'Book, school or subject'], ['board', 'Board'], ['class_level', 'Class'], ['school', 'School']].map(([key, ph]) => (
          <div key={key} className="relative">
            {key === 'q' && <Search className="w-3.5 h-3.5 text-slate-300 absolute left-2.5 top-2.5" />}
            <input value={filters[key]} placeholder={ph} data-testid={`book-shelf-filter-${key}`}
              onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
              className={`w-full rounded-lg border border-slate-200 py-2 font-body text-[12px] ${key === 'q' ? 'pl-8 pr-2' : 'px-2.5'}`} />
          </div>
        ))}
      </div>

      {loading ? (
        <p className="py-6 font-body text-sm text-slate-400 flex items-center gap-2" data-testid="book-shelf-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading the shelf…
        </p>
      ) : !rows.length ? (
        <p className="py-6 font-body text-sm text-slate-500" data-testid="book-shelf-empty">
          No uploads match that yet.
        </p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {rows.map((r, i) => {
            const id = `${r.book_name}|${r.school}|${r.class_level}|${r.subject}`;
            const isOpen = !!open[id];
            return (
              <div key={`${id}-${i}`} className="rounded-xl border border-slate-200" data-testid={`book-shelf-row-${i}`}>
                <button onClick={() => setOpen(o => ({ ...o, [id]: !isOpen }))}
                  data-testid={`book-shelf-toggle-${i}`}
                  className="w-full flex items-center gap-2 p-2.5 text-left">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-[13px] text-slate-900 truncate">
                      {r.book_name || <span className="text-amber-700">Book name not given</span>}
                    </span>
                    <span className="block font-body text-[11px] text-slate-400 truncate">
                      {r.school || 'School not given'} · {r.board?.split(' (')[0]} Class {r.class_level} · {r.subject}
                    </span>
                  </span>
                  {r.other_books_in_use > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-heading text-[10px] flex-shrink-0">
                      +{r.other_books_in_use} other book{r.other_books_in_use === 1 ? '' : 's'} here
                    </span>
                  )}
                  <span className="font-body text-[11px] text-slate-500 flex-shrink-0">
                    {r.chapter_count} ch · {r.approved}✓{r.pending ? ` · ${r.pending}⧗` : ''}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 border-t border-slate-100 pt-2" data-testid={`book-shelf-detail-${i}`}>
                    <p className="font-body text-[11px] text-slate-500">
                      {r.contributors} contributor{r.contributors === 1 ? '' : 's'} · {r.uploads} upload{r.uploads === 1 ? '' : 's'}
                      {r.rejected ? ` · ${r.rejected} rejected` : ''} · last activity {(r.last_activity || '').slice(0, 10)}
                    </p>
                    {r.contents_page_url && (
                      <a href={r.contents_page_url} target="_blank" rel="noreferrer"
                        data-testid={`book-shelf-contents-${i}`}
                        className="mt-1 inline-flex items-center gap-1 font-heading text-[11px] text-indigo-600 hover:underline">
                        Contents page <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {r.chapters.map(c => (
                        <span key={c} className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 font-body text-[11px] text-slate-600">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BookShelf;
